import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ConfirmProvider } from './components/ui';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ChatProvider } from './contexts/ChatContext';
import { LoadingProvider } from './contexts/LoadingContext';
import { SWRConfigProvider } from './contexts/SWRConfig';
import { ThemeProvider } from './contexts/ThemeContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { SettingsProvider } from './contexts/SettingsContext';
import { TopbarProvider } from './contexts/TopbarContext';
import ErrorBoundary from './components/ErrorBoundary';
import GlobalLoading from './components/GlobalLoading';
import Login from './components/Login';
import MainLayout from './components/Layout/MainLayout';
import ProtectedRoute from './components/ProtectedRoute';
import VesselAccessGate from './components/VesselAccessGate';
import AccountSettings from './modules/account-settings';
import {
  List as VesselRequisitionsList,
  Form as VesselRequisitionsForm,
  Detail as VesselRequisitionsDetail,
} from './vessel-modules/requisitions';
import {
  List as VesselPendingDeliveriesList,
  Detail as VesselPendingDeliveriesDetail,
} from './vessel-modules/pending-deliveries';
import {
  List as VesselSpareRequisitionsList,
  Form as VesselSpareRequisitionsForm,
  Detail as VesselSpareRequisitionsDetail,
} from './vessel-modules/spare-requisitions';
import {
  List as VesselSparePendingDeliveriesList,
  Detail as VesselSparePendingDeliveriesDetail,
} from './vessel-modules/spare-pending-deliveries';
import { List as VesselDashboardList } from './vessel-modules/vessel-dashboard';
import { useAccountVesselScope } from './hooks/useAccountVesselScope';
import { CrewListPage, FileView as CrewFileView } from './modules/crew';
import {
  List as VesselWrhList,
  CompliancePage as VesselWrhCompliancePage,
  WeeklyReportPage as VesselWrhWeeklyReportPage,
  RulesPage as VesselWrhRulesPage,
  RulesReportPage as VesselWrhRulesReportPage,
  ArrangementsPage as VesselWrhArrangementsPage,
  MonthSheetPage as VesselWrhMonthSheetPage,
  RestRecordReportPage as VesselWrhRestRecordReportPage,
  ComplianceReportPage as VesselWrhComplianceReportPage,
  WeeklySummaryReportPage as VesselWrhWeeklySummaryReportPage,
  OperationTypesList as VesselOperationTypesList,
  OperationTypesForm as VesselOperationTypesForm,
  OperationTypesDetail as VesselOperationTypesDetail,
  VesselOperationsSchedulePage as VesselOpsSchedulePage,
  VesselOperationsReportPage as VesselOpsReportPage,
} from './vessel-modules/wrh';
import {
  List as VesselMgaList,
  Form as VesselMgaForm,
  Dashboard as VesselMgaDashboard,
  PeriodGrid as VesselMgaPeriodGrid,
  Payslip as VesselMgaPayslip,
} from './vessel-modules/mga';

const VesselShell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <ProtectedRoute>
    <VesselAccessGate>
      <MainLayout>{children}</MainLayout>
    </VesselAccessGate>
  </ProtectedRoute>
);

const DefaultLandingRoute: React.FC = () => {
  const vesselId = useAccountVesselScope();
  if (vesselId == null) return <Navigate to="/login" replace />;
  return <Navigate to="/dashboard" replace />;
};

const AppRoutes: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-slate-500">Loading...</div>
      </div>
    );
  }

  return (
    <Routes>
      <Route
        path="/login"
        element={isAuthenticated ? <Navigate to="/" replace /> : <Login />}
      />
      <Route path="/" element={<VesselShell><DefaultLandingRoute /></VesselShell>} />
      <Route path="/dashboard" element={<VesselShell><VesselDashboardList /></VesselShell>} />
      <Route path="/requisitions" element={<VesselShell><VesselRequisitionsList /></VesselShell>} />
      <Route path="/requisitions/new" element={<VesselShell><VesselRequisitionsForm /></VesselShell>} />
      <Route path="/requisitions/:id/edit" element={<VesselShell><VesselRequisitionsForm /></VesselShell>} />
      <Route path="/requisitions/:id" element={<VesselShell><VesselRequisitionsDetail /></VesselShell>} />
      <Route path="/pending-deliveries" element={<VesselShell><VesselPendingDeliveriesList /></VesselShell>} />
      <Route
        path="/pending-deliveries/:purchaseOrderId"
        element={<VesselShell><VesselPendingDeliveriesDetail /></VesselShell>}
      />
      <Route path="/spare-requisitions" element={<VesselShell><VesselSpareRequisitionsList /></VesselShell>} />
      <Route path="/spare-requisitions/new" element={<VesselShell><VesselSpareRequisitionsForm /></VesselShell>} />
      <Route path="/spare-requisitions/:id/edit" element={<VesselShell><VesselSpareRequisitionsForm /></VesselShell>} />
      <Route path="/spare-requisitions/:id" element={<VesselShell><VesselSpareRequisitionsDetail /></VesselShell>} />
      <Route path="/spare-pending-deliveries" element={<VesselShell><VesselSparePendingDeliveriesList /></VesselShell>} />
      <Route
        path="/spare-pending-deliveries/:purchaseOrderId"
        element={<VesselShell><VesselSparePendingDeliveriesDetail /></VesselShell>}
      />
      <Route path="/crew" element={<VesselShell><CrewListPage /></VesselShell>} />
      <Route path="/crew/:id/file" element={<VesselShell><CrewFileView /></VesselShell>} />
      <Route path="/wrh" element={<VesselShell><VesselWrhList /></VesselShell>} />
      <Route path="/wrh/new" element={<Navigate to="/wrh/sheet" replace />} />
      <Route path="/wrh/compliance" element={<VesselShell><VesselWrhCompliancePage /></VesselShell>} />
      <Route path="/wrh/compliance/report" element={<VesselShell><VesselWrhComplianceReportPage /></VesselShell>} />
      <Route path="/wrh/reports" element={<VesselShell><VesselWrhWeeklyReportPage /></VesselShell>} />
      <Route path="/wrh/reports/report" element={<VesselShell><VesselWrhWeeklySummaryReportPage /></VesselShell>} />
      <Route path="/wrh/rules" element={<VesselShell><VesselWrhRulesPage /></VesselShell>} />
      <Route path="/wrh/rules/report" element={<VesselShell><VesselWrhRulesReportPage /></VesselShell>} />
      <Route path="/wrh/arrangements" element={<VesselShell><VesselWrhArrangementsPage /></VesselShell>} />
      <Route path="/wrh/sheet/rest-record" element={<VesselShell><VesselWrhRestRecordReportPage /></VesselShell>} />
      <Route path="/wrh/sheet" element={<VesselShell><VesselWrhMonthSheetPage /></VesselShell>} />
      <Route path="/wrh/operation-types" element={<VesselShell><VesselOperationTypesList /></VesselShell>} />
      <Route path="/wrh/operation-types/new" element={<VesselShell><VesselOperationTypesForm /></VesselShell>} />
      <Route path="/wrh/operation-types/:id/edit" element={<VesselShell><VesselOperationTypesForm /></VesselShell>} />
      <Route path="/wrh/operation-types/:id" element={<VesselShell><VesselOperationTypesDetail /></VesselShell>} />
      <Route path="/vessel-operations" element={<VesselShell><VesselOpsSchedulePage /></VesselShell>} />
      <Route path="/vessel-operations/report" element={<VesselShell><VesselOpsReportPage /></VesselShell>} />
      <Route path="/wrh/:id" element={<Navigate to="/wrh/sheet" replace />} />
      <Route path="/mga" element={<VesselShell><VesselMgaList /></VesselShell>} />
      <Route path="/mga/new" element={<VesselShell><VesselMgaForm /></VesselShell>} />
      <Route path="/mga/:id" element={<VesselShell><VesselMgaDashboard /></VesselShell>} />
      <Route path="/mga/:id/sheet" element={<VesselShell><VesselMgaPeriodGrid /></VesselShell>} />
      <Route path="/mga/:id/lines/:lineId" element={<VesselShell><VesselMgaPayslip /></VesselShell>} />
      <Route path="/account-settings" element={<VesselShell><AccountSettings /></VesselShell>} />
      {/* Legacy office vessel-module URLs → new vessel-app paths */}
      <Route path="/vessel-modules/:vesselId/dashboard" element={<Navigate to="/dashboard" replace />} />
      <Route path="/vessel-modules/:vesselId/requisitions" element={<Navigate to="/requisitions" replace />} />
      <Route path="/vessel-modules/:vesselId/requisitions/new" element={<Navigate to="/requisitions/new" replace />} />
      <Route
        path="/vessel-modules/:vesselId/requisitions/:id/edit"
        element={<Navigate to="/requisitions" replace />}
      />
      <Route path="/vessel-modules/:vesselId/requisitions/:id" element={<Navigate to="/requisitions" replace />} />
      <Route
        path="/vessel-modules/:vesselId/pending-deliveries"
        element={<Navigate to="/pending-deliveries" replace />}
      />
      <Route
        path="/vessel-modules/:vesselId/pending-deliveries/:purchaseOrderId"
        element={<Navigate to="/pending-deliveries" replace />}
      />
      <Route path="/vessel-modules/:vesselId/spare-requisitions" element={<Navigate to="/spare-requisitions" replace />} />
      <Route
        path="/vessel-modules/:vesselId/spare-requisitions/new"
        element={<Navigate to="/spare-requisitions/new" replace />}
      />
      <Route
        path="/vessel-modules/:vesselId/spare-requisitions/:id/edit"
        element={<Navigate to="/spare-requisitions" replace />}
      />
      <Route
        path="/vessel-modules/:vesselId/spare-requisitions/:id"
        element={<Navigate to="/spare-requisitions" replace />}
      />
      <Route
        path="/vessel-modules/:vesselId/spare-pending-deliveries"
        element={<Navigate to="/spare-pending-deliveries" replace />}
      />
      <Route
        path="/vessel-modules/:vesselId/spare-pending-deliveries/:purchaseOrderId"
        element={<Navigate to="/spare-pending-deliveries" replace />}
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

const App: React.FC = () => {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <NotificationProvider>
          <LoadingProvider>
            <Router
              future={{
                v7_startTransition: true,
                v7_relativeSplatPath: true,
              }}
            >
              <SWRConfigProvider>
                <AuthProvider>
                  <ChatProvider>
                    <SettingsProvider>
                      <TopbarProvider>
                        <ConfirmProvider>
                          <GlobalLoading />
                          <AppRoutes />
                        </ConfirmProvider>
                      </TopbarProvider>
                    </SettingsProvider>
                  </ChatProvider>
                </AuthProvider>
              </SWRConfigProvider>
            </Router>
          </LoadingProvider>
        </NotificationProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
};

export default App;
